import { useMemo, useState } from "react";
import { Link } from "react-router";
import { MessageSquareText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateBR } from "@/utils/dateUtils";
import {
  CATEGORIA_LABELS,
  type WhatsAppItem,
  type WhatsAppItemCategoria,
  type WhatsAppItemStatus,
} from "@/types/whatsapp";
import {
  WhatsAppCategoriaBadge,
  WhatsAppItemStatusBadge,
} from "./WhatsAppBadges";
import { fatoResumo } from "./WhatsAppContatoCard";
import { WhatsAppTranscriptDialog } from "./WhatsAppTranscriptDialog";

const CATEGORIAS = Object.keys(CATEGORIA_LABELS) as WhatsAppItemCategoria[];

const STATUS_FILTROS: { valor: WhatsAppItemStatus | "todos"; label: string }[] = [
  { valor: "todos", label: "Tudo" },
  { valor: "aprovado", label: "Aprovados" },
  { valor: "rejeitado", label: "Descartados" },
  { valor: "redundante", label: "Já confirmados" },
];

/** Data da decisão (ou da criação, pros redundantes que ninguém tocou). */
function dataDecisao(item: WhatsAppItem): string {
  return item.reviewed_at ?? item.created_at ?? item.observed_at;
}

function rotuloDoDia(iso: string): string {
  const dia = formatDateBR(iso);
  const hoje = formatDateBR(new Date().toISOString());
  const ontemMs = new Date().getTime() - 24 * 60 * 60 * 1000;
  const ontem = formatDateBR(new Date(ontemMs).toISOString());
  if (dia === hoje) return "Hoje";
  if (dia === ontem) return "Ontem";
  return dia;
}

function horaDe(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function WhatsAppHistorico({ items }: { items: WhatsAppItem[] }) {
  const [statusFiltro, setStatusFiltro] = useState<WhatsAppItemStatus | "todos">(
    "todos",
  );
  const [categoriaFiltro, setCategoriaFiltro] = useState<
    WhatsAppItemCategoria | "todas"
  >("todas");
  const [transcriptItem, setTranscriptItem] = useState<WhatsAppItem | null>(null);

  const porDia = useMemo(() => {
    const visiveis = items.filter(
      (item) =>
        (statusFiltro === "todos" || item.review_status === statusFiltro) &&
        (categoriaFiltro === "todas" || item.categoria === categoriaFiltro),
    );
    const dias = new Map<string, WhatsAppItem[]>();
    for (const item of visiveis) {
      const chave = dataDecisao(item).slice(0, 10);
      const doDia = dias.get(chave);
      if (doDia) doDia.push(item);
      else dias.set(chave, [item]);
    }
    return [...dias.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([chave, itensDoDia]) => ({
        chave,
        itens: itensDoDia.sort((a, b) =>
          dataDecisao(b).localeCompare(dataDecisao(a)),
        ),
      }));
  }, [items, statusFiltro, categoriaFiltro]);

  const contagemPorStatus = useMemo(() => {
    const contagem: Record<string, number> = {};
    for (const item of items) {
      contagem[item.review_status] = (contagem[item.review_status] ?? 0) + 1;
    }
    return contagem;
  }, [items]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTROS.map((filtro) => (
          <Button
            key={filtro.valor}
            variant={statusFiltro === filtro.valor ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFiltro(filtro.valor)}
          >
            {filtro.label}
            {filtro.valor !== "todos" && ` (${contagemPorStatus[filtro.valor] ?? 0})`}
            {filtro.valor === "todos" && ` (${items.length})`}
          </Button>
        ))}
        <span className="mx-1 h-5 w-px bg-border" />
        <Button
          variant={categoriaFiltro === "todas" ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setCategoriaFiltro("todas")}
        >
          Todas categorias
        </Button>
        {CATEGORIAS.map((categoria) => (
          <Button
            key={categoria}
            variant={categoriaFiltro === categoria ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setCategoriaFiltro(categoria)}
          >
            {CATEGORIA_LABELS[categoria]}
          </Button>
        ))}
      </div>

      {porDia.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          Nada revisado ainda com esse filtro.
        </div>
      ) : (
        porDia.map((dia) => (
          <div key={dia.chave} className="space-y-2">
            <div className="sticky top-0 z-10 bg-background/95 py-1 text-sm font-semibold backdrop-blur-sm">
              {rotuloDoDia(dia.itens[0] ? dataDecisao(dia.itens[0]) : dia.chave)}
              <span className="ml-2 font-normal text-muted-foreground">
                {dia.itens.length}{" "}
                {dia.itens.length === 1 ? "registro" : "registros"}
              </span>
            </div>
            <Card>
              <CardContent className="divide-y p-0">
                {dia.itens.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm"
                  >
                    <span className="w-11 shrink-0 text-xs text-muted-foreground">
                      {horaDe(dataDecisao(item))}
                    </span>
                    <WhatsAppCategoriaBadge categoria={item.categoria} />
                    <span className="min-w-0 shrink-0 font-medium">
                      {item.freelancer_id ? (
                        <Link
                          to={`/freelancers/${item.freelancer_id}`}
                          className="text-primary hover:underline"
                        >
                          {item.freelancer_nome ?? "Freelancer"}
                        </Link>
                      ) : (
                        (item.contact_name ?? item.telefone ?? "Contato")
                      )}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">
                      {fatoResumo(item)}
                    </span>
                    <WhatsAppItemStatusBadge status={item.review_status} />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1.5 px-2 text-muted-foreground"
                      onClick={() => setTranscriptItem(item)}
                      title="Ver conversa"
                    >
                      <MessageSquareText className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        ))
      )}

      <WhatsAppTranscriptDialog
        conversationId={transcriptItem?.conversation_id ?? null}
        highlightIds={transcriptItem?.message_ids ?? []}
        onClose={() => setTranscriptItem(null)}
      />
    </div>
  );
}
